import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { answered } from 'src/components/property/UtilityPortalCredentials/asyncResult';
import { UtilityCredentialStatusLine } from 'src/components/property/UtilityPortalCredentials/UtilityCredentialStatusLine';
import { findCredentialForUtility } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { currentCompanyId } from 'src/components/property/UtilityPortalCredentials/currentCompanyId';
import { canManageUtilityCredentials } from 'src/utils/canManageUtilityCredentials';

/**
 * The portal credential for the utility being processed, on the Process modal.
 *
 * Whoever is entering this bill is the person who just logged into that portal, so the credential's
 * health belongs on this screen and not only on property details.
 *
 * Company-scoped fetch, not per property: the billing dashboard runs across all of a company's
 * properties, so one request covers every row an operator will open. Matching is still per utility.
 */
export const ProcessCredentialLine = observer(_ref => {
  let propertyUtilityId = _ref.propertyUtilityId;
  const store = useStore();
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    credentials = _useState2[0],
    setCredentials = _useState2[1];
  // Distinct from "no credentials". The model resolves undefined rather than rejecting on an API
  // failure, so without tracking this the row would read "No portal credentials saved" whenever the
  // request simply did not come back.
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    unavailable = _useState4[0],
    setUnavailable = _useState4[1];
  const canManageCredentials = canManageUtilityCredentials();
  const companyId = currentCompanyId();
  useEffect(() => {
    if (!canManageCredentials || !companyId) {
      setCredentials([]);
      return undefined;
    }
    let isStale = false;
    store.admin.getUtilityCredentialsAsync.run({
      companyId
    }).then(resp => {
      if (isStale) return;
      const rows = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      // Entering a bill must never depend on the credential service being up, so a failure is
      // recorded and rendered as nothing rather than surfaced as an error on someone else's form.
      // A superseded run resolves undefined without setting isError, and reading that as an empty
      // list would claim this utility has no credentials saved when we never got an answer.
      setUnavailable(!answered(resp) || store.admin.getUtilityCredentialsAsync.isError);
      setCredentials(Array.isArray(rows) ? rows : []);
    }).catch(() => {
      if (isStale) return;
      setUnavailable(true);
      setCredentials([]);
    });
    return () => {
      isStale = true;
    };
  }, [canManageCredentials, companyId, store]);

  // Gated on the render as well as the fetch, so a role that cannot manage credentials never sees the heading.
  if (!canManageCredentials) return null;

  // Say nothing rather than "no credentials saved" when we simply could not ask.
  if (unavailable) return null;

  // Deliberately no same-provider fallback: findCredentialForUtility matches propertyUtilityId only,
  // because a looser match would show one property's login against another property's utility.
  const credential = findCredentialForUtility(credentials, propertyUtilityId);

  // A property-scoped credential can exist without an account row tying it to this utility, so a
  // miss here is normal rather than an error worth shouting about.
  if (!credential && !propertyUtilityId) return null;
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 0.5,
    sx: {
      marginBottom: '12px'
    },
    "data-testid": "process-credential-line"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Portal credential"), /*#__PURE__*/React.createElement(UtilityCredentialStatusLine, {
    credential: credential,
    isVisible: true
  }));
});